'use client';

import React, { useId, useState } from 'react';
import {
  CalendarBlankIcon,
  CaretDownIcon,
  ClockIcon,
  DotsThreeVerticalIcon,
  ListChecksIcon,
  ShareNetworkIcon,
  UserIcon,
} from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Menu, MenuItem } from '@tranquility-ai/theme-components/menu';
import { Modal } from '@tranquility-ai/theme-components/modal';
import { StatCard } from '@tranquility-ai/theme-components/stat-card';
import { StatusPill } from '@tranquility-ai/theme-components/status-pill';
import { Tag } from '@tranquility-ai/theme-components/tag';
import { Text } from '@tranquility-ai/theme-components/primitives';
import { ThreatReportShareModal } from '@/components/threat-report-share-modal';
import { useCaseThreatReports } from '@/hooks/useCaseThreatReports';
import { useThreatReportCreators } from '@/hooks/useThreatReportCreators';
import { useOptionalI18n } from '@/i18n/LocaleProvider';
import { DEFAULT_APP_LOCALE } from '@/i18n/locales';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';
import type { ThreatReportSummary } from '@/shared/cors-edge-lambda-api';
import {
  formatThreatReportDateTime,
  orderThreatReportVersions,
  summarizeThreatReportSelectors,
  threatReportCreatorUsername,
  threatReportSelectorDetails,
  threatReportCreatedAt,
  threatReportExportDateKind,
  threatReportExportStamp,
  type ThreatReportExportDateKind,
  type ThreatReportSelectorDetail,
  type ThreatReportSelectorSummary,
} from '@/shared/threat-report-identity';
import type { CaseThreatReport } from '@/shared/threat-report-sharing';
import {
  rememberRequestedThreatReportLanguages,
  resolveThreatReportHeaderVersion,
  threatReportVersions,
  type RequestedThreatReportLanguages,
} from '@/shared/threat-report-version-group';
import type { ThreatReportKey } from '@/shared/types';

import './threat-report-version-header.css';

/**
 * Identity cards for the threat report on screen, plus the version picker the
 * work-product nav renders beside the title.
 *
 * Versions are reruns of the same selector set, in the same language, on any
 * export. They are told apart by Fivecast export time. App-local composition (candidate for
 * promotion to theme-components). The cards sit outside the exported report body.
 */
export function summaryToThreatReportKey(summary: ThreatReportSummary): ThreatReportKey {
  return {
    chatKey: summary.chatKey,
    sessionId: summary.sessionId,
    epochTimeSecs: summary.epochTimeSecs,
    requestType: summary.requestType,
  };
}

const ENGLISH_FALLBACK_TITLE = /^Threat Report — (.+)$/;

/** Localized stand-in for the English title the API uses when a report has none. */
export function displayThreatReportTitle(title: string, t: TranslateFn): string {
  const match = ENGLISH_FALLBACK_TITLE.exec(title.trim());
  if (!match?.[1]) return title;
  return t('threatReport.fallbackTitle', { stamp: match[1] });
}

/** Title of the version on screen, for the work-product nav. */
export function useThreatReportNavTitle(
  caseId: string | undefined,
  currentEpochTimeSecs: number | undefined,
  currentChatKey: string | undefined,
): string | undefined {
  const t = useTimePilotT();
  const { reports } = useCaseThreatReports(caseId);
  const title = resolveThreatReportHeaderVersion(reports, currentEpochTimeSecs, currentChatKey)?.title;
  return title ? displayThreatReportTitle(title, t) : undefined;
}

function formatCreatedAt(
  report: Pick<CaseThreatReport, 'timestamp' | 'epochTimeSecs'>,
  t: TranslateFn,
): string {
  const createdAt = threatReportCreatedAt(report);
  return createdAt ? formatThreatReportDateTime(createdAt) : t('threatReport.header.dateUnavailable');
}

function formatExportValue(
  report: Pick<CaseThreatReport, 'fivecastDataDirectory'>,
  uploadedAtByUrl: Record<string, string>,
  t: TranslateFn,
): string {
  if (!report.fivecastDataDirectory) return t('threatReport.header.noFivecastExport');
  const stamp = threatReportExportStamp(report, uploadedAtByUrl);
  return stamp
    ? formatThreatReportDateTime(stamp.at, stamp.precision)
    : t('threatReport.header.dateUnavailable');
}

/** Names the clock the card is showing: export date, export date and time, or upload time. */
function exportCardLabel(kind: ThreatReportExportDateKind, t: TranslateFn): string {
  if (kind === 'uploaded') return t('threatReport.header.exportUploadedLabel');
  if (kind === 'filename-time') return t('threatReport.header.exportDateTimeLabel');
  if (kind === 'missing') return t('threatReport.header.exportLabel');
  return t('threatReport.header.exportDateLabel');
}

type ThreatReportGenerationReport = Pick<
  CaseThreatReport,
  'fivecastDataDirectory' | 'timestamp' | 'epochTimeSecs' | 'status'
>;

/**
 * Work-product description: the same clock the export card names.
 * The report-created time stays on the card and the Created column.
 */
export function threatReportGenerationDescription(
  report: ThreatReportGenerationReport,
  uploadedAtByUrl: Record<string, string>,
  t: TranslateFn,
): string {
  if (report.status === 'inprogress') return t('threatReport.processing');
  if (report.status === 'error') return t('threatReport.generationError');
  const kind = threatReportExportDateKind(report, uploadedAtByUrl);
  if (kind === 'missing') return t('threatReport.header.noFivecastExport');
  return `${exportCardLabel(kind, t)}: ${formatExportValue(report, uploadedAtByUrl, t)}`;
}

/** Picker copy: the kind of date, then the date, so two versions are not just two clocks. */
function exportVersionPhrase(report: CaseThreatReport, uploadedAtByUrl: Record<string, string>, t: TranslateFn): string {
  const kind = threatReportExportDateKind(report, uploadedAtByUrl);
  if (kind === 'missing') return t('threatReport.header.noFivecastExport');
  const date = formatExportValue(report, uploadedAtByUrl, t);
  if (kind === 'uploaded') return t('threatReport.header.versionUploaded', { date });
  if (kind === 'filename-day') return t('threatReport.header.versionExportDate', { date });
  if (kind === 'filename-time') return t('threatReport.header.versionExportDateTime', { date });
  return date;
}

/** Closed picker: the export's date, plus a short clock time when the stamp has one. */
function versionClosedLabel(report: CaseThreatReport, uploadedAtByUrl: Record<string, string>, t: TranslateFn): string {
  if (!report.fivecastDataDirectory) return t('threatReport.header.noFivecastExport');
  const stamp = threatReportExportStamp(report, uploadedAtByUrl);
  return stamp
    ? formatThreatReportDateTime(stamp.at, stamp.precision)
    : t('threatReport.header.dateUnavailable');
}

/**
 * StatCard's label is a string, so the badge cannot sit in the header row.
 * Below 500px the badge is its own line above the heading. From 500px up it
 * stays in the top corner, on the heading's line.
 * `variant="text"` is the 16/24 semibold value, and the library lines that
 * value up with the label when an icon is present.
 */
function IdentityCard({
  label,
  icon,
  action,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const card = (
    <StatCard
      tone="inverse"
      variant="text"
      label={label}
      icon={icon}
      value={children}
      className="tp-threat-report-identity-card h-full min-w-0"
    />
  );
  if (!action) return card;
  return (
    <div className="tp-threat-report-identity h-full min-w-0">
      <div className="tp-threat-report-identity__frame">
        <div className="tp-threat-report-identity__action">{action}</div>
        {card}
      </div>
    </div>
  );
}

function SelectorDetails({
  details,
  allAccounts,
  t,
}: {
  details: ThreatReportSelectorDetail[];
  allAccounts: boolean;
  t: TranslateFn;
}) {
  return (
    <div className="flex flex-col gap-3 pr-4">
      {allAccounts && (
        <Text as="p" variant="bodySmall">
          {t('threatReport.header.allAccounts')}
        </Text>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-sm leading-5">
          <thead>
            <tr className="border-b border-[color:var(--color-ink-075,#1a263e)]">
              <th scope="col" className="px-2 py-2 font-semibold">{t('threatReport.header.selectorColumnName')}</th>
              <th scope="col" className="px-2 py-2 font-semibold">{t('threatReport.header.selectorColumnPlatform')}</th>
              <th scope="col" className="px-2 py-2 font-semibold">{t('threatReport.header.selectorColumnHandles')}</th>
              <th scope="col" className="px-2 py-2 text-right font-semibold">{t('threatReport.header.selectorColumnActivity')}</th>
            </tr>
          </thead>
          <tbody>
            {details.map((selector) => (
              <tr key={selector.id} className="border-b border-[color:var(--color-ink-075,#1a263e)]">
                <th scope="row" className="px-2 py-2 font-medium">{selector.name}</th>
                <td className="px-2 py-2">{selector.platform || '—'}</td>
                <td className="px-2 py-2">{selector.handles.join(', ') || '—'}</td>
                <td className="px-2 py-2 text-right">{selector.activityCount ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SelectorsCard({
  summary,
  details,
  loading,
  t,
}: {
  summary: ThreatReportSelectorSummary;
  details: ThreatReportSelectorDetail[];
  loading: boolean;
  t: TranslateFn;
}) {
  const [open, setOpen] = useState(false);
  const label =
    summary.count === null
      ? t('threatReport.header.selectorsLabel')
      : t('threatReport.header.selectorsLabelCount', { count: summary.count });
  const namesPending = loading && summary.count !== null && !summary.allAccounts;

  let value: React.ReactNode;
  if (summary.count === null) {
    value = (
      <Text as="span" variant="bodySmall" weight="normal">
        {loading ? t('threatReport.header.selectorsLoading') : t('threatReport.header.selectorsUnavailable')}
      </Text>
    );
  } else if (namesPending) {
    value = (
      <Text as="span" variant="bodySmall" weight="normal">
        {t('threatReport.header.selectorsLoading')}
      </Text>
    );
  } else {
    value = (
      <div className="flex flex-col items-start gap-2 font-normal">
        {summary.allAccounts && (
          <Text as="span" variant="bodyTiny" weight="normal">
            {t('threatReport.header.allAccounts')}
          </Text>
        )}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="threat-report-selectors">
          {summary.names.map((name, index) => (
            <Tag key={`${name}:${index}`} label={name} inverse title={name} />
          ))}
          {summary.remaining > 0 && (
            <Tag
              inverse
              label={
                summary.remaining === 1
                  ? t('threatReport.header.selectorsOverflowOne')
                  : t('threatReport.header.selectorsOverflowMany', { count: summary.remaining })
              }
            />
          )}
        </div>
      </div>
    );
  }

  const showAll = summary.labels.length > 0 && !namesPending && summary.count !== null;

  return (
    <>
      <IdentityCard
        label={label}
        icon={<ListChecksIcon weight="fill" />}
        action={
          showAll ? (
            <Button
              type="button"
              size="sm"
              style="text"
              color="light"
              background="inverse"
              onClick={() => setOpen(true)}
            >
              {t('threatReport.header.viewAllSelectors')}
            </Button>
          ) : undefined
        }
      >
        {value}
      </IdentityCard>
      <Modal
        isOpen={open}
        onOpenChange={setOpen}
        title={t('threatReport.header.selectorsDialogTitle')}
        size="lg"
      >
        <SelectorDetails details={details} allAccounts={summary.allAccounts} t={t} />
      </Modal>
    </>
  );
}

export interface ThreatReportVersionHeaderProps {
  caseId: string | undefined;
  /** Chat key of the version on screen, so two analysts' reports do not collide. */
  currentChatKey?: string;
  /** Epoch of the version on screen. Absent means the newest completed report. */
  currentEpochTimeSecs?: number;
  /**
   * S3 upload time keyed by export URL (``getSources`` ``sourceModifiedAt``).
   * Dates an export whose filename carries no creation stamp.
   */
  sourceModifiedAt?: Record<string, string>;
}

const NO_UPLOAD_TIMES: Record<string, string> = {};

export function ThreatReportVersionHeader({
  caseId,
  currentChatKey,
  currentEpochTimeSecs,
  sourceModifiedAt = NO_UPLOAD_TIMES,
}: ThreatReportVersionHeaderProps) {
  const t = useTimePilotT();
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [sharingReport, setSharingReport] = useState<CaseThreatReport | null>(null);
  const { reports } = useCaseThreatReports(caseId);

  const current = resolveThreatReportHeaderVersion(reports, currentEpochTimeSecs, currentChatKey);
  const exportSource = current?.fivecastDataDirectory ?? undefined;
  const creatorsQuery = useThreatReportCreators(caseId, exportSource);

  if (!current) return null;

  const selectors = summarizeThreatReportSelectors(current.creatorIds, creatorsQuery.data);
  const selectorsLoading = Boolean(exportSource) && creatorsQuery.isLoading;
  const exportKind = threatReportExportDateKind(current, sourceModifiedAt);
  const canShare = current.isOwner && current.status === 'completed';

  return (
    <div
      role="region"
      aria-label={t('threatReport.header.regionLabel')}
      className="tp-threat-report-header"
    >
    <div className="tp-threat-report-header__grid">
      <IdentityCard label={exportCardLabel(exportKind, t)} icon={<CalendarBlankIcon weight="fill" />}>
        {formatExportValue(current, sourceModifiedAt, t)}
      </IdentityCard>
      <IdentityCard label={t('threatReport.header.createdLabel')} icon={<ClockIcon weight="fill" />}>
        {formatCreatedAt(current, t)}
      </IdentityCard>
      <SelectorsCard
        summary={selectors}
        details={threatReportSelectorDetails(current.creatorIds, creatorsQuery.data)}
        loading={selectorsLoading}
        t={t}
      />
      <IdentityCard
        label={t('threatReport.header.creatorLabel')}
        icon={<UserIcon weight="fill" />}
        action={
          <span className="flex items-center gap-1">
            <StatusPill inverse size="sm" color={current.shared ? 'brand' : 'neutral'}>
              {current.shared ? t('threatReport.header.shared') : t('threatReport.header.notShared')}
            </StatusPill>
            {canShare ? (
              <Menu
                isOpen={shareMenuOpen}
                onOpenChange={setShareMenuOpen}
                inverted
                align="end"
                aria-label={t('threatReport.header.creatorActions', { title: current.title })}
                content={
                  <MenuItem
                    icon={<ShareNetworkIcon />}
                    onSelect={() => setSharingReport(current)}
                  >
                    {current.shared
                      ? t('threatReport.header.manageSharing')
                      : t('threatReport.header.share')}
                  </MenuItem>
                }
              >
                <Button
                  type="button"
                  iconOnly
                  size="sm"
                  style="text"
                  color="light"
                  background="inverse"
                  aria-haspopup="menu"
                  aria-expanded={shareMenuOpen}
                  aria-label={t('threatReport.header.creatorActions', { title: current.title })}
                  leadingIcon={<DotsThreeVerticalIcon weight="bold" />}
                  onClick={() => setShareMenuOpen((open) => !open)}
                />
              </Menu>
            ) : null}
          </span>
        }
      >
        <span className="block truncate" title={current.createdBy ?? undefined}>
          {threatReportCreatorUsername(current.createdBy) || t('threatReport.header.creatorUnknown')}
        </span>
      </IdentityCard>
    </div>
      <ThreatReportShareModal
        caseId={caseId}
        report={sharingReport}
        onClose={() => setSharingReport(null)}
      />
    </div>
  );
}

export interface ThreatReportVersionPickerProps {
  caseId: string | undefined;
  currentChatKey?: string;
  currentEpochTimeSecs?: number;
  sourceModifiedAt?: Record<string, string>;
  onSelectVersion: (key: ThreatReportKey) => void;
}

/** "Version:" plus the export date (and a short time, when the export has one). */
export function ThreatReportVersionPicker({
  caseId,
  currentChatKey,
  currentEpochTimeSecs,
  sourceModifiedAt = NO_UPLOAD_TIMES,
  onSelectVersion,
}: ThreatReportVersionPickerProps) {
  const t = useTimePilotT();
  const locale = useOptionalI18n()?.locale ?? DEFAULT_APP_LOCALE;
  const labelId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const { reports } = useCaseThreatReports(caseId);
  const [requestedLanguages, setRequestedLanguages] = useState<RequestedThreatReportLanguages>(() => new Map());
  const rememberedLanguages = rememberRequestedThreatReportLanguages(requestedLanguages, reports, locale);
  if (rememberedLanguages !== requestedLanguages) setRequestedLanguages(rememberedLanguages);
  const current = resolveThreatReportHeaderVersion(reports, currentEpochTimeSecs, currentChatKey);

  if (!current) return null;

  const listed = orderThreatReportVersions(
    threatReportVersions(reports, current, rememberedLanguages),
    sourceModifiedAt,
  );
  const trigger = versionClosedLabel(current, sourceModifiedAt, t);
  const versionLabel = t('threatReport.header.versionLabel');

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span id={labelId} className="shrink-0 text-sm font-semibold text-white" aria-hidden>
        {versionLabel}
      </span>
      <Menu
          isOpen={menuOpen}
          onOpenChange={setMenuOpen}
          inverted
          align="end"
          aria-label={t('threatReport.header.versionsMenuLabel')}
          title={t('threatReport.header.versionsMenuTitle')}
          content={
            <>
              {listed.map((report) => {
                const isCurrent =
                  report.chatKey === current.chatKey && report.epochTimeSecs === current.epochTimeSecs;
                return (
                  <MenuItem
                    key={`${report.chatKey}:${report.epochTimeSecs}`}
                    checked={isCurrent}
                    description={t('threatReport.header.versionCreated', {
                      date: formatCreatedAt(report, t),
                    })}
                    onSelect={() => {
                      if (!isCurrent) onSelectVersion(summaryToThreatReportKey(report));
                    }}
                  >
                    {exportVersionPhrase(report, sourceModifiedAt, t)}
                  </MenuItem>
                );
              })}
            </>
          }
        >
          <Button
            type="button"
            size="md"
            style="solid"
            color="secondary"
            background="inverse"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`${versionLabel} ${trigger}`}
            trailingIcon={<CaretDownIcon weight="bold" />}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {trigger}
          </Button>
        </Menu>
    </div>
  );
}
